<script lang="ts">
 /**
 * Top bar showing the active container context during capture.
 *
 * @remarks
 * Displays ancestor breadcrumbs and current container name. Tapping the
 * container name opens the container picker drawer; the back button exits
 * capture mode entirely.
 *
 * Falls back to "No container selected" when no container is set.
 *
 * Uses explicit dark values (bg-neutral-950, text-white) rather than semantic
 * tokens — camera UIs use dark chrome to reduce glare and light bleed.
 */
</script>

<script setup lang="ts">
import { ICON } from "@/lib/icons"
import type { BreadcrumbSegment } from "@/types/models"

defineProps<{
  /** Ancestor breadcrumb segments from root to current container's parent. */
  ancestors: BreadcrumbSegment[]
  /** Display name of the active container, or null if none selected. */
  containerName: string | null
}>()

defineEmits<{
  /** Fired when the user taps the container name area. Opens the picker. */
  openPicker: []
  /** Fired when the user taps the back arrow. Exits capture mode. */
  back: []
}>()
</script>

<!-- Dark chrome: camera UIs use dark bars to reduce glare and light bleed.
     Uses explicit dark values rather than semantic tokens, which are designed
     for the standard light/dark app surfaces. -->
<template>
  <div
    class="flex items-center gap-2 bg-black px-4"
    :style="{
      height: 'calc(4rem + var(--safe-top))',
      paddingTop: 'var(--safe-top)',
    }"
  >
    <UButton
      :icon="ICON.back"
      aria-label="Exit capture"
      color="neutral"
      variant="ghost"
      size="lg"
      square
      class="shrink-0 rounded-full text-white"
      @click="$emit('back')"
    />

    <button
      v-if="containerName"
      class="flex min-w-0 flex-1 items-center gap-2"
      @click="$emit('openPicker')"
    >
      <div class="flex min-w-0 flex-1 flex-col items-center">
        <p v-if="ancestors.length" class="max-w-full truncate text-xs text-white/50">
          <template v-for="(segment, i) in ancestors" :key="segment.id">
            <span>{{ segment.name }}</span>
            <span v-if="i < ancestors.length - 1"> › </span>
          </template>
        </p>
        <span class="truncate text-sm font-semibold text-white">{{ containerName }}</span>
      </div>
      <UIcon :name="ICON.chevronDown" class="shrink-0 text-white/50" />
    </button>

    <div v-else class="flex-1 text-sm text-white/50">No container selected</div>
  </div>
</template>
